import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/TopBar";
import { useMe } from "@/components/auth/RequireAuth";
import { getMatches, deleteMatch } from "@/lib/api";

const STATUS = { setup: "Por iniciar", live: "Em curso", finished: "Terminado" };

const MatchRow = ({ m, i, onDelete, showOwner }) => (
  <li data-testid={`match-row-${m.id}`} style={{ animationDelay: `${i * 50}ms` }}
    className="rise group grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_140px_120px_auto] items-center gap-4 py-5 border-b border-white/10">
    <Link to={m.status === "finished" ? `/jogo/${m.id}/relatorio` : `/jogo/${m.id}`} data-testid={`match-open-${m.id}`} className="min-w-0">
      <p className="font-display text-2xl sm:text-3xl font-bold uppercase truncate group-hover:text-[#CCFF00]">
        {m.home.name} <span className="text-zinc-600">vs</span> {m.away.name}
      </p>
      <p className="text-sm text-zinc-500 truncate">{[m.competition, m.date, m.venue].filter(Boolean).join(" · ") || "Sem detalhes"}</p>
      {showOwner && m.owner_email && <p data-testid={`match-owner-${m.id}`} className="text-xs text-[#CCFF00]/80 truncate mt-0.5">{m.owner_email}</p>}
    </Link>
    <span className="hidden sm:block label-caps">{STATUS[m.status]}</span>
    <span className="hidden sm:block font-display text-2xl font-bold tabular-nums">{m.events.length} <span className="text-sm text-zinc-500">eventos</span></span>
    <div className="flex gap-1">
      <Link to={`/jogo/${m.id}/relatorio`} data-testid={`match-report-${m.id}`} className="tap w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5">
        <ArrowUpRight size={18} />
      </Link>
      <button data-testid={`match-delete-${m.id}`} onClick={() => onDelete(m.id)} className="tap w-10 h-10 flex items-center justify-center text-zinc-500 hover:text-[#FF3B30] hover:bg-white/5">
        <Trash2 size={18} />
      </button>
    </div>
  </li>
);

export default function Home() {
  const [matches, setMatches] = useState(null);
  const me = useMe();
  const navigate = useNavigate();

  useEffect(() => { getMatches().then(setMatches).catch(() => setMatches([])); }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminar este jogo?")) return;
    await deleteMatch(id);
    setMatches((ms) => ms.filter((m) => m.id !== id));
    toast.success("Jogo eliminado");
  };

  return (
    <div className="relative z-10">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end mb-16">
          <div className="rise">
            <p className="label-caps mb-4">Registo de eventos de jogo</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.9]">
              Cada lance,<br /><span className="text-[#CCFF00]">ao segundo.</span>
            </h1>
          </div>
          <div className="rise" style={{ animationDelay: "120ms" }}>
            <p className="text-zinc-400 mb-6">Configure as equipas, inicie o cronómetro e registe faltas, cartões, vantagens e foras de jogo diretamente no mapa do campo.</p>
            <button data-testid="new-match-btn" onClick={() => navigate("/novo")}
              className="tap h-16 px-8 bg-[#CCFF00] text-black font-display text-xl font-bold uppercase flex items-center gap-3 hover:brightness-110">
              <Plus size={22} /> Novo jogo
            </button>
          </div>
        </div>
        <div className="flex items-baseline justify-between border-b border-white/15 pb-3">
          <h2 className="font-display text-2xl font-bold uppercase">{me.role === "admin" ? "Histórico · todos os utilizadores" : "Os meus jogos"}</h2>
          <span data-testid="match-count" className="label-caps">{matches?.length ?? 0} jogos</span>
        </div>
        {matches && !matches.length && <p data-testid="history-empty" className="text-zinc-500 py-10">Ainda não há jogos registados.</p>}
        <ul data-testid="match-list">
          {matches?.map((m, i) => <MatchRow key={m.id} m={m} i={i} onDelete={remove} showOwner={me.role === "admin"} />)}
        </ul>
      </main>
    </div>
  );
}
